import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdPerson,
  MdEvent,
  MdStore
} from "react-icons/md";
import { FaWarehouse } from "react-icons/fa6";
import DashboardService from "services/dashboard.service";
import Toast from "components/toaster";
import Loader from "../../../assets/img/loader.gif";
import tabCache from "utils/tabCache";
import { MdPayment } from "react-icons/md";


const Dashboard = () => {
  const navigate = useNavigate();

  // Dashboard data state - initialize from cache if available
  const [dashboardData, setDashboardData] = useState(tabCache.dashboard.data || null);
  // Initialize from cache - show loader only on first load
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectedCountryCode, setSelectedCountryCode] = useState("all");
  const [commissionData, setCommissionData] = useState(null);
  const [showCommission, setShowCommission] = useState(false);
  const [loadingCommission, setLoadingCommission] = useState(false);

  // Load dates from localStorage on mount
  useEffect(() => {
    // Ensure country code is never persisted (cleanup old stored value).
    localStorage.removeItem("dashboard-country-code");

    const savedStartDate = localStorage.getItem("dashboard-start-date");
    const savedEndDate = localStorage.getItem("dashboard-end-date");

    if (savedStartDate) {
      setSelectedStartDate(new Date(savedStartDate));
    }
    if (savedEndDate) {
      setSelectedEndDate(new Date(savedEndDate));
    }
  }, []);

  // Listen for date range changes from Navbar
  useEffect(() => {
    const handleDateRangeChange = (event) => {
      const { startDate, endDate, countryCode } = event.detail;
      setSelectedStartDate(startDate);
      setSelectedEndDate(endDate);
      setSelectedCountryCode(countryCode ?? "all");
    };

    window.addEventListener('dateRangeChanged', handleDateRangeChange);
    return () => {
      window.removeEventListener('dateRangeChanged', handleDateRangeChange);
    };
  }, []);

  // Fetch dashboard data
  const fetchDashboardData = async (startDate = null, endDate = null, countryCode = "all") => {
    const isFirstLoad = tabCache.dashboard.firstLoad;

    // Show loader only on first load
    if (isFirstLoad) {
      setLoading(true);
    }
    setError(null);

    try {
      let payload = {};

      // If dates are provided, format them and include in payload
      if (startDate && endDate) {
        const formattedStartDate = new Date(startDate);
        formattedStartDate.setHours(0, 0, 0, 0);

        const formattedEndDate = new Date(endDate);
        formattedEndDate.setHours(23, 59, 59, 999);

        payload = {
          startDate: formattedStartDate.toISOString(),
          endDate: formattedEndDate.toISOString(),
        };
      }

      if (countryCode && countryCode !== "all") {
        payload = {
          ...payload,
          filters: {
            users: {
              filters: {
                country_code: countryCode,
              },
            },
          },
        };
      }

      const response = await DashboardService.getDashboardCounts(payload);

      if (response?.data?.status) {
        const dashboardDataResult = response.data.data;

        // Update state and cache
        setDashboardData(dashboardDataResult);
        tabCache.dashboard.data = dashboardDataResult;

        // After first API call, don't show loader next time
        if (isFirstLoad) {
          tabCache.dashboard.firstLoad = false;
        }
      } else {
        throw new Error(response?.data?.message || "Failed to fetch dashboard data");
      }
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      const errorMessage = err.message || "Failed to fetch dashboard data";
      setError(errorMessage);
      Toast("error", errorMessage);
      if (isFirstLoad) {
        tabCache.dashboard.firstLoad = false;
      }
    } finally {
      if (isFirstLoad) {
        setLoading(false);
      }
    }
  };

  // Fetch all data on component mount and when date range changes
  useEffect(() => {
    // If we have cached data, show it immediately and fetch in background
    if (tabCache.dashboard.data) {
      // Data already shown from initial state, just fetch in background
      if (selectedStartDate && selectedEndDate) {
        fetchDashboardData(selectedStartDate, selectedEndDate, selectedCountryCode);
      } else {
        fetchDashboardData(null, null, selectedCountryCode);
      }
    } else {
      // No cached data, fetch and show loader
      if (selectedStartDate && selectedEndDate) {
        fetchDashboardData(selectedStartDate, selectedEndDate, selectedCountryCode);
      } else {
        fetchDashboardData(null, null, selectedCountryCode);
      }
    }
    // eslint-disable-next-line
  }, [selectedStartDate, selectedEndDate, selectedCountryCode]);

  const getDateQueryParams = () => {
    if (selectedStartDate && selectedEndDate) {
      const start = new Date(selectedStartDate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(selectedEndDate);
      end.setHours(23, 59, 59, 999);

      return {
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      };
    }

    return {};
  };

  const navigateWithDate = (path, extraParams = {}) => {
    const params = new URLSearchParams();

    Object.entries(extraParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, value);
      }
    });

    Object.entries(getDateQueryParams()).forEach(([key, value]) => {
      params.set(key, value);
    });

    const queryString = params.toString();
    navigate(queryString ? `${path}?${queryString}` : path);
  };

  // Fetch commission data only when user clicks to reveal
  const fetchCommissionData = async () => {
    setLoadingCommission(true);
    setError(null);

    try {
      let payload = {};

      // If dates are provided, format them and include in payload
      if (selectedStartDate && selectedEndDate) {
        const formattedStartDate = new Date(selectedStartDate);
        formattedStartDate.setHours(0, 0, 0, 0);

        const formattedEndDate = new Date(selectedEndDate);
        formattedEndDate.setHours(23, 59, 59, 999);

        payload = {
          startDate: formattedStartDate.toISOString(),
          endDate: formattedEndDate.toISOString(),
        };
      }

      const commissionResponse = await DashboardService.getCommission(payload);
      if (commissionResponse?.data?.status) {
        const commData = commissionResponse.data.data;
        setCommissionData(commData);
      } else {
        throw new Error(commissionResponse?.data?.message || "Failed to fetch commission data");
      }
    } catch (err) {
      console.error("Error fetching commission data:", err);
      const errorMessage = err.message || "Failed to fetch commission data";
      setError(errorMessage);
      Toast("error", errorMessage);
    } finally {
      setLoadingCommission(false);
    }
  };

  // Normalised ticketing data (supports both old and new response shapes)
  const ticketingData = dashboardData?.ticketing || dashboardData?.ticketRevenue || null;

  return (
    <div className="h-full w-full bg-navy-900 px-3 sm:px-6  pb-2">
      {/* Metric Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8 mt-4 sm:-mt-1 w-full">
        {loading && (
          <div className="col-span-full flex justify-center items-center min-h-[80vh]">
            <img
              src={Loader}
              alt="Loading..."
              className="h-16 w-16"
            />
          </div>
        )}

        {!loading && error && (
          <div className="col-span-full flex justify-center items-center py-12">
            <div className="text-red-500 text-lg">{error}</div>
          </div>
        )}

        {!loading && dashboardData && (
          <>
            {/* Users Card */}
            <div className="bg-navy-800 rounded-xl p-4 border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-lg min-h-[230px]">
              <div className="flex items-center mb-4 mt-1">
                <div className="p-2 ml-4 rounded-full bg-white/5 text-white mr-3 border  border-white/10">
                  <MdPerson className=" h-7 w-7" />
                </div>
                <h3 className="text-xl font-bold text-white ">
                  Users
                </h3>
              </div>
              <div className="pl-1 pr-1 pt-1 pb-1">

                <div
                  className="group flex justify-between items-center hover:bg-[#FFFFFF]/10 hover:rounded-lg px-2 py-1 transition-colors cursor-pointer"
                  onClick={() => navigateWithDate("/users")}
                >
                  <p className="text-base text-[#8E8E8E] group-hover:text-white transition-colors">Total Users </p>
                  <p className="text-lg font-regular text-[#8E8E8E] group-hover:text-white transition-colors">{dashboardData?.users?.allActive || 0}</p>
                </div>

                <div
                  className="group flex justify-between items-center hover:bg-[#FFFFFF]/10 hover:rounded-lg px-2 py-1 transition-colors cursor-pointer"
                  onClick={() => navigateWithDate("/users",{user_type:"guestUsers"})}
                >
                  <p className="text-base text-[#8E8E8E] group-hover:text-white transition-colors">Guest Users </p>
                  <p className="text-lg font-regular text-[#8E8E8E] group-hover:text-white transition-colors">{dashboardData?.users?.guestUsers || 0}</p>
                </div>
                <div
                  className="group flex justify-between items-center hover:bg-[#FFFFFF]/10 hover:rounded-lg px-2 py-1 transition-colors cursor-pointer"
                  onClick={() => navigateWithDate("/users", { user_type: "normalActive" })}
                >
                  <p className="text-base text-[#8E8E8E] group-hover:text-white transition-colors">Normal Users</p>
                  <p className="text-lg font-regular text-[#8E8E8E] group-hover:text-white transition-colors">{dashboardData?.users?.normalActive || 0}</p>
                </div>
                <div
                  className="group flex justify-between items-center hover:bg-[#FFFFFF]/10 hover:rounded-lg px-2 py-1 transition-colors cursor-pointer"
                  onClick={() => navigateWithDate("/users", { user_type: "proActive" })}
                >
                  <p className="text-base text-[#8E8E8E] group-hover:text-white transition-colors">Pro Users </p>
                  <p className="text-lg font-regular text-[#8E8E8E] group-hover:text-white transition-colors">{dashboardData?.users?.proActive || 0}</p>
                </div>
             
                <div
                  className="group flex justify-between items-center hover:bg-[#FFFFFF]/10 hover:rounded-lg px-2 py-1 transition-colors cursor-pointer"
                  onClick={() => navigateWithDate("/users", { user_type: "organizationActive" })}
                >
                  <p className="text-base text-[#8E8E8E] group-hover:text-white transition-colors">Organization Users </p>
                  <p className="text-lg font-regular text-[#8E8E8E] group-hover:text-white transition-colors">{dashboardData?.users?.organizationActive || 0}</p>
                </div>
              </div>
            </div>

            {/* Events Card */}
            <div className="bg-navy-800 rounded-xl p-4 border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-lg min-h-[230px]">
              <div className="flex items-center mb-4 mt-1">
                <div className="p-2 ml-4 rounded-full bg-white/5 text-white mr-3 border  border-white/10">
                  <MdEvent className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-bold text-white  ">
                  Events
                </h3>
              </div>
              <div className="pl-2 pr-2">
                <div
                  className="group flex justify-between items-center  hover:bg-[#FFFFFF]/10 hover:rounded-lg px-2 py-1 transition-colors cursor-pointer"
                  onClick={() => navigateWithDate("/explore", { event_type: "futureAll" })}
                >
                  <p className="text-base text-[#8E8E8E] group-hover:text-white transition-colors">Total Events </p>
                  <p className="text-lg font-regular text-[#8E8E8E] group-hover:text-white transition-colors">{dashboardData?.events?.futureAll || 0}</p>
                </div>
                <div
                  className="group flex justify-between items-center  hover:bg-[#FFFFFF]/10 hover:rounded-lg px-2 py-1 transition-colors cursor-pointer"
                  onClick={() => navigateWithDate("/explore", { event_type: "futureBoosted" })}
                >
                  <p className="text-base text-[#8E8E8E] group-hover:text-white transition-colors">Boosted Events</p>
                  <p className="text-lg font-regular text-[#8E8E8E] group-hover:text-white transition-colors">{dashboardData?.events?.futureBoosted || 0}</p>
                </div>
                <div
                  className="group flex justify-between items-center  hover:bg-[#FFFFFF]/10 hover:rounded-lg px-2 py-1 transition-colors cursor-pointer"
                  onClick={() => navigateWithDate("/explore", { event_type: "futureAdmin" })}
                >
                  <p className="text-base text-[#8E8E8E] group-hover:text-white transition-colors">Admin Events </p>
                  <p className="text-lg font-regular text-[#8E8E8E] group-hover:text-white transition-colors">{dashboardData?.events?.futureAdmin || 0}</p>
                </div>
              </div>
            </div>

            {/* Garages Card */}
            <div className="bg-navy-800 rounded-xl p-4 border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-lg min-h-[230px]">
              <div className="flex items-center mb-4 mt-1">
                <div className="p-2 ml-4 rounded-full bg-white/5 text-white mr-3 border border-white/10">
                  <FaWarehouse className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-white ">
                  Garages
                </h3>
              </div>
              <div className="pl-2 pr-2">
                <div
                  className="group flex justify-between items-center  hover:bg-[#FFFFFF]/10 hover:rounded-lg px-2 py-1 transition-colors cursor-pointer"
                  onClick={() => navigateWithDate("/explore", { tab: "garages" })}
                >
                  <p className="text-base text-[#8E8E8E] group-hover:text-white transition-colors">All Active Garages</p>
                  <p className="text-lg font-regular text-[#8E8E8E] group-hover:text-white transition-colors">{dashboardData?.garages?.allActive || 0}</p>
                </div>
              </div>
            </div>

            {/* Vendors Card */}
            <div className="bg-navy-800 rounded-xl p-4 border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-lg min-h-[230px]">
              <div className="flex items-center mb-4 mt-1">
                <div className="p-2 rounded-full ml-4 bg-white/5 text-white mr-3 border border-white/10">
                  <MdStore className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-bold text-white ">
                  Vendor Requests
                </h3>
              </div>
              <div className="pl-2 pr-2">
                <div
                  className="group flex justify-between items-center  hover:bg-[#FFFFFF]/10 hover:rounded-lg px-2 py-1 transition-colors cursor-pointer"
                  onClick={() => navigateWithDate("/vendor", { tab: "requests" })}
                >
                  <p className="text-base text-[#8E8E8E] group-hover:text-white transition-colors">Total Requests </p>
                  <p className="text-lg font-regular text-[#8E8E8E] group-hover:text-white transition-colors">{dashboardData?.vendors?.allRequests || 0}</p>
                </div>
                <div
                  className="group flex justify-between items-center  hover:bg-[#FFFFFF]/10 hover:rounded-lg px-2 py-1 transition-colors cursor-pointer"
                  onClick={() => navigateWithDate("/vendor", { status: "approved" })}
                >
                  <p className="text-base text-[#8E8E8E] group-hover:text-white transition-colors">Approved Vendors </p>
                  <p className="text-lg font-regular text-[#8E8E8E] group-hover:text-white transition-colors">{dashboardData?.vendors?.approved || 0}</p>
                </div>
                <div
                  className="group flex justify-between items-center  hover:bg-[#FFFFFF]/10 hover:rounded-lg px-2 py-1 transition-colors cursor-pointer"
                  onClick={() => navigateWithDate("/vendor", { status: "rejected" })}
                >
                  <p className="text-base text-[#8E8E8E] group-hover:text-white transition-colors">Rejected Vendors</p>
                  <p className="text-lg font-regular text-[#8E8E8E] group-hover:text-white transition-colors">{dashboardData?.vendors?.rejected || 0}</p>
                </div>
              </div>
            </div>

            {/* Ticket Revenue Card */}
            <div className="bg-navy-800 rounded-xl p-4 border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-lg min-h-[230px]">
              <div className="flex items-center mb-4 mt-1">
                <div className="p-2 rounded-full ml-4 bg-white/5 text-white mr-3 border border-white/10">
                  <MdPayment className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-bold text-white">
                  Ticket Revenue
                </h3>
              </div>

              <div className="pl-2 pr-2 ">

                <div
                  className="group flex justify-between items-center hover:bg-[#FFFFFF]/10 hover:rounded-lg px-2 py-1 transition-colors cursor-pointer"
                  onClick={() => navigateWithDate("/ticket-revenue")}
                >
                  <p className="text-base text-[#8E8E8E] group-hover:text-white transition-colors">
                    Total Tickets Sold
                  </p>
                  <p className="text-lg text-[#8E8E8E] group-hover:text-white transition-colors">
                    {ticketingData?.totalTicketsSold ??
                      ticketingData?.totalTickets ??
                      0}
                  </p>
                </div>

                <div
                  className="group flex justify-between items-center hover:bg-[#FFFFFF]/10 hover:rounded-lg px-2 py-1 transition-colors"
                >
                  <p className="text-base text-[#8E8E8E] group-hover:text-white transition-colors">
                    Commission
                  </p>
                  {showCommission ? (
                    loadingCommission ? (
                      <div className="h-5 w-5 flex items-center justify-center">
                        <div className="h-4 w-4 border-2 border-[#8E8E8E] border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : (
                      <p className="text-lg text-[#8E8E8E] group-hover:text-white transition-colors">
                        {typeof commissionData === 'object'
                          ? (commissionData?.commission ?? commissionData?.totalCommission ?? 0)
                          : (commissionData ?? ticketingData?.commission ?? 0)}
                      </p>
                    )
                  ) : (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        // If revealing and data not fetched yet, fetch it first
                        if (commissionData === null && !loadingCommission) {
                          setShowCommission(true); // Show loading state
                          await fetchCommissionData();
                        } else {
                          setShowCommission(true);
                        }
                      }}
                      className="text-xs px-2 py-1 bg-white/10 hover:bg-white/20 text-[#8E8E8E] hover:text-white rounded transition-colors"
                    >
                      View
                    </button>
                  )}
                </div>



              </div>
            </div>


          </>
        )}
      </div>

    </div>
  );
};

export default Dashboard;
